/* eslint-disable max-statements */

import type { RouteLocationAsRelative } from 'vue-router'

import { ROUTE_ADMIN } from '~/contexts/admin-tenant/router/routes'
import { ROUTE_AURA } from '~/contexts/aura/router/routes'
import { ROUTE_CALLS } from '~/contexts/calls/router/routes'
import { ROUTE_COMMUNITY } from '~/contexts/community/router/routes'
import { ROUTE_DM } from '~/contexts/dm/router/routes'
import { ROUTE_HOME } from '~/contexts/home/router/routes'
import { ROUTE_ME } from '~/contexts/me/router/routes'
import { ROUTE_NETWORK } from '~/contexts/network/router/routes'
import { ROUTE_RITUAL } from '~/contexts/ritual/router/routes'

import { useLastVisitedAIConversation } from '~/features-shared/aura/stores/states'
import { useLastVisitedCommunity } from '~/features-shared/community/stores/states'
import { useContextManager } from '~/features-shared/config-manager/composables/context-manager'
import { EContext } from '~/features-shared/config-manager/interfaces/contexts'
import { useLastVisitedConversation } from '~/features-shared/conversation/stores/conversation-states'
import { useLastVisitedMyUniverse } from '~/features-shared/me/stores/states'

import { EXALEAD_SEARCH_DEBOUNCE } from '~/features-generic/config/exalead-query'
import type { NavigationOptions } from '~/features-generic/left-panel/interfaces/left-panel'

/** from 1 to 639px, the thread panel is in overlay mode */
export const OVERLAY_MAX_WIDTH = 639

/** Return true if the left panel is NOT pushing the main column */
export const isOverlayActivated = () => window.innerWidth <= OVERLAY_MAX_WIDTH

/** Active (onHover) context of the left column */
export const activeContext = ref<EContext>(EContext.NULL)

/** Pinned (clicked) context of the left column */
export const pinnedContext = ref<EContext>(EContext.COMMUNITY)

/** Context of the left column, return active or pinned context */
export const currentContext = refDebounced(computed(() => activeContext.value || pinnedContext.value), 50)

const [isPanelActive, togglePanel] = useToggle(false)
const [isPanelPinned, togglePanelPin] = useToggle(false)
const isPanelOpened = refDebounced(computed(() => isPanelActive.value || isPanelPinned.value), 100)

export {
  isPanelOpened,
  isPanelPinned,
  isPanelActive,
  togglePanel,
  togglePanelPin,
}

let init = false

/**
 * navigate of the left column
 * @see NavigationOptions
 */
// eslint-disable-next-line complexity
export async function navigate(navOpts: NavigationOptions): Promise<void> {
  const { router } = await import('~/router/router')
  const _context = navOpts.context || (activeContext.value || pinnedContext.value)
  if (navOpts.onlyIfSameContext && navOpts.context !== (activeContext.value || pinnedContext.value))
    return

  if (_context === EContext.COMMUNITY) {
    const lastVisitedCommunity = useLastVisitedCommunity()
    if (navOpts.id) {
      lastVisitedCommunity.value = navOpts.id
      if (navOpts.id === 'whatsnew') {
        router.push({
          name: ROUTE_HOME.HOME,
          replace: navOpts.replace,
        })
      }
      else {
        router.push({
          name: ROUTE_COMMUNITY.URI_HOME,
          params: { thread_id: navOpts.id },
          replace: navOpts.replace,
        })
      }
    }
    else {
      return navigate({
        id: lastVisitedCommunity.value,
        context: navOpts.context,
        replace: navOpts.replace,
      })
    }
  }
  else if (_context === EContext.ADMIN) {
    router.push({
      name: ROUTE_ADMIN.ADMIN_TENANT,
      replace: navOpts.replace,
    })
  }
  else if (_context === EContext.RITUALS) {
    if (navOpts.id && navOpts.id !== 'whatsnew') {
      router.push({
        name: ROUTE_RITUAL.URI_HOME,
        params: { thread_id: navOpts.id },
        replace: navOpts.replace,
      })
    }
    else {
      router.push({
        name: ROUTE_RITUAL.HOME,
        replace: navOpts.replace,
      })
    }
  }
  else if (_context === EContext.DM) {
    if (navOpts.id && navOpts.id !== 'whatsnew') {
      useLastVisitedConversation().value = navOpts.id
      router.push({
        name: ROUTE_DM.URI_HOME,
        params: { thread_id: navOpts.id },
        replace: navOpts.replace,
      })
    }
    else {
      router.push({
        name: ROUTE_DM.HOME,
        replace: navOpts.replace,
      })
    }
  }
  else if (_context === EContext.AURA) {
    if (navOpts.id && navOpts.id !== 'whatsnew') {
      useLastVisitedAIConversation().value = navOpts.id
      router.push({
        name: ROUTE_AURA.URI_HOME,
        params: { thread_id: navOpts.id },
        replace: navOpts.replace,
      })
    }
    else {
      router.push({
        name: ROUTE_AURA.HOME,
        replace: navOpts.replace,
      })
    }
  }
  else if (_context === EContext.ME) {
    if (navOpts.id) {
      const lastVisitedMyUniverse = useLastVisitedMyUniverse()
      lastVisitedMyUniverse.value = navOpts.id as ROUTE_ME
      router.push({
        name: navOpts.id as ROUTE_ME,
        replace: navOpts.replace,
      } as RouteLocationAsRelative)
    }
    else {
      router.push({ name: ROUTE_ME.HOME, replace: navOpts.replace })
    }
  }
  else if (_context === EContext.NETWORK) {
    if (navOpts.id) {
      if (navOpts.id === 'network') {
        router.push({
          name: ROUTE_NETWORK.ACTIVITY,
          replace: navOpts.replace,
        })
      }
      else if (navOpts.id === 'invitations') {
        router.push({
          name: ROUTE_NETWORK.INVITATIONS,
          replace: navOpts.replace,
        })
      }
      else {
        router.push({
          name: ROUTE_NETWORK.USER_ACTIVITY,
          params: { thread_id: navOpts.id },
          replace: navOpts.replace,
        })
      }
    }
    else {
      router.push({
        name: ROUTE_NETWORK.ACTIVITY,
        replace: navOpts.replace,
      })
    }
  }
  else if (_context === EContext.CALLS) {
    router.push({
      name: ROUTE_CALLS.HOME,
      replace: navOpts.replace,
    })
  }
  togglePanel(false)
}

/**
 * Change the pinned context and reset to null active context
 * @param event A context
 * @param nav If true, will navigate (through the router) to the context
 */
export function togglePinContext(event = EContext.NULL, nav: boolean) {
  if (event === EContext.ADMIN) {
    navigate({
      id: '',
      context: event,
    })
    return
  }
  if (pinnedContext.value === event) {
    togglePanelPin()
    togglePanel(isPanelPinned.value)
  }
  else {
    pinnedContext.value = event
    activeContext.value = EContext.NULL
    togglePanel(true)
    togglePanelPin(true)
    if (nav) {
      navigate({
        id: '',
        context: event,
      })
      togglePanelPin(true)
    }
  }
}

/**
 * Change active context and display the panel (onHover) if context is not null
 * @param event A context
 */
export function toggleActiveContext(event = EContext.NULL) {
  activeContext.value = event

  if (event !== EContext.NULL)
    togglePanel(true)
  else togglePanel(false)
}

/**
 * Display the selected context as pinned and remove active (onHover) context
 * @param event A context
 */
export function displayContext(event: EContext) {
  pinnedContext.value = event
  activeContext.value = EContext.NULL
}

const _searchVal = ref('')

/** Left panel current search, throttle by "EXALEAD_SEARCH_DEBOUNCE" (~1s) to avoid too much calls */
const searchVal = refThrottled(_searchVal, EXALEAD_SEARCH_DEBOUNCE, true)

/**
 * Use this to update the search value of the left panel
 * @param v the search value
 */
export function search(v: string) {
  _searchVal.value = v
  if (v)
    togglePanelPin(true)
}

/**
 * Use left panel manager to know which item is selected
 * @see selected
 */
export function useLeftPanelManager() {
  const route = useRoute()
  const { context } = useContextManager()

  if (!init) {
    pinnedContext.value = context.value || EContext.COMMUNITY

    togglePanelPin(!isOverlayActivated())
    init = true
  }

  // IR-1049543 When the context changes (because the route changed),
  // update the displayed context to reflect the middle column context
  whenever(context, displayContext)

  /** The selected item "id" (could be a route name or a subjectUri) in the current left panel */
  const selected = computed<string | undefined>(() => {
    const routeName = route.name

    if (context.value === currentContext.value) {
      if (currentContext.value === EContext.ME)
        return routeName

      if (currentContext.value === EContext.NETWORK) {
        const selectedThread = routeName === ROUTE_NETWORK.INVITATIONS ? 'invitations' : 'network'
        return ('thread_id' in route.params && (route.params.thread_id as string)) || selectedThread
      }

      return ('thread_id' in route.params && (route.params.thread_id as string))
        || (routeName === 'index' ? '' : 'whatsnew')
    }
  })

  return {
    /** @see searchVal */
    searchVal: readonly(searchVal),
    /** The selected item "id" (could be a route name or a subjectUri) in the current left panel */
    selected,
  }
}
